import { useEffect, useState } from "react";
import { UserX, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import ModalShell from "@/components/shared/ModalShell";

/** Bouton « Désactiver le suivi » (MEDECIN / IDE assignés) — motif obligatoire, journalisé côté backend. */
export default function DeactivatePatientButton({ patient, onDeactivated }) {
  const [open, setOpen] = useState(false);
  if (!patient || patient.isActive === false) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="deactivate-patient-btn"
        className="flex items-center gap-2 bg-white text-rose-700 border border-rose-200 rounded-xl px-4 py-2 text-sm font-medium hover:bg-rose-50 transition-colors"
      >
        <UserX className="w-4 h-4" />
        Désactiver le suivi
      </button>
      {open && (
        <DeactivatePatientModal
          patient={patient}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); onDeactivated?.(); }}
        />
      )}
    </>
  );
}

export function DeactivatePatientModal({ patient, onClose, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/patients/lifecycle/deactivation-reasons")
      .then((res) => setReasons(res.data?.items || []))
      .catch(() => setReasons([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!reasonCode) return toast.error("Choisissez un motif");
    if (reasonCode === "OTHER" && !reasonText.trim()) return toast.error("Précisez le motif");
    setLoading(true);
    try {
      await api.patch(`/patients/${patient.id}/deactivate`, { reasonCode, reasonText: reasonText.trim() || null });
      toast.success("Suivi désactivé — l'administrateur de la clinique est informé");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de désactiver le suivi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell testId="deactivate-patient-modal">
      <div className="p-5 border-b bg-rose-50 rounded-t-2xl flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <UserX className="w-5 h-5 text-rose-600" /> Désactiver le suivi
        </h2>
        <button type="button" onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg" data-testid="deactivate-patient-close">
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>
      <form onSubmit={submit} className="p-5 space-y-4">
        <p className="text-sm text-slate-600">
          {patient.firstName} {patient.lastName} ne sera plus suivi(e) dans HANA : plus de rappels, d'alertes ni de facturation.
          Seul l'administrateur de la clinique pourra réactiver le suivi.
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Motif *</label>
          <select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            data-testid="deactivate-reason-select"
          >
            <option value="">— Choisir —</option>
            {reasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Précision {reasonCode === "OTHER" ? "*" : "(optionnel)"}</label>
          <textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            maxLength={500}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none h-20"
            data-testid="deactivate-reason-text"
          />
        </div>
        <div className="flex gap-3 pt-3 border-t">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium">Annuler</button>
          <button
            type="submit"
            disabled={loading}
            data-testid="deactivate-patient-submit"
            className="flex-1 py-2.5 bg-rose-600 text-white rounded-xl font-medium hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmer la désactivation
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
